import { useSyncExternalStore } from "react"

// One live call per browser, not per tab (ENG-3391). Tab-local state cannot see a
// session running next door, so a second tab dialled straight over the first and
// Twilio registered the same identity twice. The tab holding a session stamps a
// lock in localStorage and renews it on a heartbeat; every other tab reads it and
// greys its own dial controls out. The heartbeat is what makes a crashed or
// force-quit tab release: a stamp older than STALE_MS is nobody's.
const LOCK_KEY = "orbitshift.voice.call-lock"
const RENEW_MS = 2000
const STALE_MS = 6000

const TAB_ID =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.floor(Math.random() * 1e9)}`

type Lock = { tabId: string; at: number }

function readLock(): Lock | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(LOCK_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Lock>
    return typeof parsed?.tabId === "string" && typeof parsed?.at === "number"
      ? { tabId: parsed.tabId, at: parsed.at }
      : null
  } catch {
    return null
  }
}

export function otherTabOnCall() {
  const lock = readLock()
  if (!lock || lock.tabId === TAB_ID) return false
  return Date.now() - lock.at < STALE_MS
}

export function claimCallLock() {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(
      LOCK_KEY,
      JSON.stringify({ tabId: TAB_ID, at: Date.now() })
    )
  } catch {
    return
  }
}

// Only ever clears our own stamp — releasing somebody else's would hand a third
// tab a licence to dial over a live call.
export function releaseCallLock() {
  if (typeof window === "undefined") return
  try {
    const lock = readLock()
    if (!lock || lock.tabId === TAB_ID) window.localStorage.removeItem(LOCK_KEY)
  } catch {
    return
  }
}

// A single subscription shared by every dial control on the page. Each row in the
// people list renders one, so a per-component interval and storage listener would
// mean fifty of each.
let held = false
const listeners = new Set<() => void>()
let timer: ReturnType<typeof setInterval> | null = null

function refresh() {
  const next = otherTabOnCall()
  if (next === held) return
  held = next
  listeners.forEach((notify) => notify())
}

function subscribe(notify: () => void) {
  listeners.add(notify)
  if (!timer) {
    refresh()
    // `storage` fires in the other tabs the moment a lock is written or cleared,
    // so this is the fast path; the interval only exists to notice a heartbeat
    // that stopped, which fires no event at all.
    window.addEventListener("storage", refresh)
    timer = setInterval(refresh, RENEW_MS)
  }
  return () => {
    listeners.delete(notify)
    if (listeners.size || !timer) return
    clearInterval(timer)
    timer = null
    window.removeEventListener("storage", refresh)
  }
}

export function useOtherTabOnCall() {
  return useSyncExternalStore(
    subscribe,
    () => held,
    () => false
  )
}

export function holdCallLock(active: boolean) {
  if (!active) return undefined
  claimCallLock()
  const renew = setInterval(claimCallLock, RENEW_MS)
  window.addEventListener("pagehide", releaseCallLock)
  return () => {
    clearInterval(renew)
    window.removeEventListener("pagehide", releaseCallLock)
    releaseCallLock()
  }
}
